// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useRef } from 'react'
import { Sprout, SolarPanel, Waves, Droplets, Fuel, Zap, Battery, ArrowDownToLine, AirVent, Pipette, Heater, PlugZap, Home, Flame, ShieldCheck, Filter, Wrench, Camera, Bell, Lock, AlertTriangle } from 'lucide-react'
import { supabase } from '../supabase'
import TaskDetailSheet from './TaskDetailSheet'
import { compressImage } from '../imageUtils'
import { systemSchedules } from './AddSystem'
import { recordProvenance } from '../utils/photoProvenance'
import { stripExif } from '../utils/stripExif'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}

function getManualUrl(make, model) {
  const clean = (s) => (s || '').replace(/[/\\]/g, ' ').trim()
  const q = encodeURIComponent(`${clean(make)} ${clean(model)} manual filetype:pdf OR site:manualslib.com OR site:manua.ls`)
  return `https://www.google.com/search?q=${q}`
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return dateStr }
}

function calcNextDue(lastDate, interval) {
  if (!lastDate || !interval) return null
  const d = new Date(lastDate)
  const i = interval.toLowerCase()
  if (i.includes('every use') || i.includes('each use')) return null
  if (i.includes('month') && i.includes('3')) d.setDate(d.getDate() + 90)
  else if (i.includes('month') && i.includes('6')) d.setDate(d.getDate() + 180)
  else if (i.includes('month')) d.setDate(d.getDate() + 30)
  else if (i.includes('90')) d.setDate(d.getDate() + 90)
  else if (i.includes('2 year')) d.setDate(d.getDate() + 730)
  else if (i.includes('year') || i.includes('annual')) d.setDate(d.getDate() + 365)
  else if (i.includes('180')) d.setDate(d.getDate() + 180)
  else if (i.includes('week')) d.setDate(d.getDate() + 7)
  else return null
  return d.toISOString().split('T')[0]
}

function getDueStatus(nextDue) {
  if (!nextDue) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((new Date(nextDue) - today) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'overdue'
  if (diffDays <= 30) return 'soon'
  return 'good'
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px', fontFamily: 'Georgia, serif',
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%',
    maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(44,29,20,0.2)', border: `1px solid ${C.border}`,
  },
  header: { padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '22px', fontWeight: '700', color: C.brown },
  closeBtn: { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1 },
  body: { padding: '20px 28px 28px' },
  photoBox: {
    width: '100%', height: '200px', borderRadius: '12px',
    background: C.bg, border: `1px solid ${C.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '20px', overflow: 'hidden', position: 'relative',
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoPlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' },
  photoActions: { position: 'absolute', bottom: '10px', right: '10px', display: 'flex', gap: '6px' },
  photoBtn: { background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '6px 12px', fontSize: '11px', color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif' },
  photoUploading: { position: 'absolute', inset: 0, background: 'rgba(253,250,246,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: C.amber, fontFamily: 'Calibri, sans-serif' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
  detailItem: { background: C.bg, borderRadius: '10px', padding: '12px 14px', border: `1px solid ${C.border}` },
  detailLabel: { fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px', fontFamily: 'Calibri, sans-serif' },
  detailValue: { fontSize: '14px', fontWeight: '600', color: C.brown },
  detailEmpty: { fontSize: '13px', color: C.muted, fontStyle: 'italic' },
  sectionTitle: { fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif' },
  divider: { borderTop: `1px solid ${C.border}`, margin: '20px 0' },
  scheduleRow: (hovered) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '11px 10px', borderBottom: `1px solid ${C.border}`,
    borderRadius: '8px', cursor: 'pointer',
    background: hovered ? '#FBF3E8' : 'transparent',
    transition: 'background 0.15s', margin: '0 -10px',
  }),
  tag: (type) => {
    const colors = { auto_order: { bg: '#FBF3E8', color: '#B8721F' }, contractor: { bg: '#EBF5EF', color: '#2E6B4A' }, diy: { bg: '#F2EDE4', color: '#9A7B68' } }
    const c = colors[type] || colors.diy
    return { fontSize: '10px', padding: '3px 10px', borderRadius: '20px', background: c.bg, color: c.color, fontFamily: 'Calibri, sans-serif', border: `1px solid ${c.color}44`, whiteSpace: 'nowrap' }
  },
  howToHint: { fontSize: '10px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '2px' },
  notesBox: { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '11px 14px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none', resize: 'vertical', minHeight: '80px', marginBottom: '8px' },
  saveNotesBtn: { background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '7px 16px', fontSize: '12px', color: C.amber, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '20px' },
  editBtn: { flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase' },
  saveBtn: { flex: 1, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '10px', padding: '12px', fontSize: '13px', color: C.card, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '700' },
  deleteBtn: { background: 'transparent', border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: C.danger, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase' },
  input: { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none', marginBottom: '12px' },
  select: { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none', marginBottom: '12px', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' },
  alert: (type) => ({ borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', background: type === 'error' ? C.dangerBg : C.successBg, border: `1px solid ${type === 'error' ? C.danger : C.success}44`, color: type === 'error' ? C.danger : C.success }),
  statusBadge: (status) => ({ display: 'inline-block', fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontFamily: 'Calibri, sans-serif', background: status === 'good' ? '#EBF5EF' : '#FBF3E8', color: status === 'good' ? C.success : C.amber, border: `1px solid ${status === 'good' ? C.success : C.amber}44` }),
}

const systemIcons = {
  septic: Sprout, solar: SolarPanel, pool: Waves, well: Droplets, propane: Fuel,
  generator: Zap, battery: Battery, sump_pump: ArrowDownToLine, whole_house_fan: AirVent,
  water_softener: Pipette, radiant_heat: Heater, electrical_panel: PlugZap, adu: Home, other: Wrench,
  fireplace: Flame, fire_sprinkler: ShieldCheck, air_filtration: Filter,
  security_system: ShieldCheck, security_cameras: Camera, smart_doorbell: Bell,
  smart_locks: Lock, smoke_co_detectors: AlertTriangle,
}

const SECURITY_TYPES = ['security_system', 'security_cameras', 'smart_doorbell', 'smart_locks', 'smoke_co_detectors']

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: currentYear - 1979 }, (_, i) => currentYear - i)

export default function SystemDetail({ system, onClose, onUpdated, onPhotoUpdated, onDeleted }) {
  const [mode, setMode] = useState('view')
  const [notes, setNotes] = useState(system.notes || '')
  const [notesSaved, setNotesSaved] = useState(false)
  const [loading, setLoading] = useState(false)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [hoveredTask, setHoveredTask] = useState(null)
  const [selectedTask, setSelectedTask] = useState(null)
  const [taskDates, setTaskDates] = useState(system.task_dates || {})
  const [markingDone, setMarkingDone] = useState(null)
  const [photos, setPhotos] = useState(system.photos || [])
  const MAX_PHOTOS = 5
  const [manualUrl, setManualUrl] = useState(system.manual_url || null)
  const [manualUploading, setManualUploading] = useState(false)
  const fileInputRef = useRef(null)
  const manualInputRef = useRef(null)

  const [editForm, setEditForm] = useState({
    manufacturer: system.manufacturer || '',
    model: system.model || '',
    installed_year: system.installed_year || '',
    last_serviced: system.last_serviced || '',
  })
  const [securityData, setSecurityData] = useState({
    monitoring_type: system.monitoring_type || '',
    monthly_cost: system.monthly_cost || '',
    contract_expiration: system.contract_expiration || '',
    alarm_company_phone: system.alarm_company_phone || '',
    camera_count: system.camera_count || '',
    sensor_count: system.sensor_count || '',
    last_inspection: system.last_inspection || '',
    alarm_code_hint: system.alarm_code_hint || '',
    has_locking_mailbox: system.has_locking_mailbox || false,
  })

  const schedule = systemSchedules[system.type] || []
  const SysIcon = systemIcons[system.type] || Wrench

  // ── PHOTO GALLERY ─────────────────────────────────────────────────────────
  const handlePhotoClick = () => {
    if (photos.length >= MAX_PHOTOS) { setAlert({ type: 'error', msg: `Maximum ${MAX_PHOTOS} photos allowed.` }); return }
    fileInputRef.current?.click()
  }

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 20 * 1024 * 1024) { setAlert({ type: 'error', msg: 'Photo must be under 20MB.' }); return }
    if (photos.length >= MAX_PHOTOS) { setAlert({ type: 'error', msg: `Maximum ${MAX_PHOTOS} photos allowed.` }); return }
    setPhotoUploading(true)
    setAlert(null)
    try {
      const userId = system.user_id
      const stripped = await stripExif(file)
      const compressed = await compressImage(stripped)
      const fileName = `${userId}/systems/${system.id}_${Date.now()}.jpg`
      const { error: uploadError } = await supabase.storage.from('photos').upload(fileName, compressed, { upsert: true, contentType: 'image/jpeg' })
      if (uploadError) throw uploadError
      const { data } = supabase.storage.from('photos').getPublicUrl(fileName)
      const url = data.publicUrl
      const newPhotos = [...photos, url]
      // Only update photos array — never touch photo_url (stock image)
      const { error: updateError } = await supabase.from('systems').update({ photos: newPhotos }).eq('id', system.id)
      if (updateError) throw updateError
      recordProvenance({
        file: compressed,
        originalFile: file,
        storageBucket: 'photos',
        storagePath: fileName,
        resourceType: 'system',
        resourceId: system.id,
        homeId: system.home_id || null,
        userId,
      }).catch(() => {})
      setPhotos(newPhotos)
      onPhotoUpdated ? onPhotoUpdated() : onUpdated()
    } catch (err) {
      setAlert({ type: 'error', msg: 'Error uploading photo. Please try again.' })
    }
    setPhotoUploading(false)
    e.target.value = ''
  }

  const handleRemovePhoto = async (urlToRemove) => {
    setPhotoUploading(true)
    const newPhotos = photos.filter(p => p !== urlToRemove)
    await supabase.from('systems').update({ photos: newPhotos }).eq('id', system.id)
    setPhotos(newPhotos)
    setPhotoUploading(false)
    onPhotoUpdated ? onPhotoUpdated() : onUpdated()
  }

  // ── MANUAL UPLOAD ─────────────────────────────────────────────────────────
  const handleManualUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { setAlert({ type: 'error', msg: 'File must be under 50MB.' }); return }
    setManualUploading(true)
    setAlert(null)
    try {
      const userId = system.user_id
      const fileName = `${userId}/manuals/system_${system.id}.pdf`
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, file, { upsert: true, contentType: file.type })
      if (uploadError) throw uploadError
      const { data } = supabase.storage.from('documents').getPublicUrl(fileName)
      const { error: updateError } = await supabase.from('systems').update({ manual_url: data.publicUrl }).eq('id', system.id)
      if (updateError) throw updateError
      setManualUrl(data.publicUrl)
      onUpdated()
    } catch {
      setAlert({ type: 'error', msg: 'Error uploading manual. Please try again.' })
    }
    setManualUploading(false)
    e.target.value = ''
  }

  // ── SAVE / EDIT / DELETE ──────────────────────────────────────────────────
  const saveNotes = async () => {
    setLoading(true)
    const { error } = await supabase.from('systems').update({ notes }).eq('id', system.id)
    setLoading(false)
    if (!error) { setNotesSaved(true); setTimeout(() => setNotesSaved(false), 2000); onUpdated() }
    else setAlert({ type: 'error', msg: 'Error saving notes.' })
  }

  const saveEdit = async () => {
    setLoading(true)
    const { error } = await supabase.from('systems').update({
      manufacturer: editForm.manufacturer,
      model: editForm.model,
      installed_year: editForm.installed_year ? parseInt(editForm.installed_year) : null,
      last_serviced: editForm.last_serviced,
    }).eq('id', system.id)
    setLoading(false)
    if (!error) { setMode('view'); setAlert({ type: 'success', msg: 'System updated.' }); onUpdated() }
    else setAlert({ type: 'error', msg: 'Error updating system.' })
  }

  const deleteSystem = async () => {
    setLoading(true)
    const { error } = await supabase.from('systems').delete().eq('id', system.id)
    setLoading(false)
    if (!error) onDeleted()
    else setAlert({ type: 'error', msg: 'Error removing system.' })
  }

  const setEdit = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }))

  const saveSecurityData = async () => {
    setLoading(true)
    const update = {
      monitoring_type: securityData.monitoring_type || null,
      monthly_cost: securityData.monthly_cost ? parseFloat(securityData.monthly_cost) : null,
      contract_expiration: securityData.contract_expiration || null,
      alarm_company_phone: securityData.alarm_company_phone || null,
      camera_count: securityData.camera_count ? parseInt(securityData.camera_count) : null,
      sensor_count: securityData.sensor_count ? parseInt(securityData.sensor_count) : null,
      last_inspection: securityData.last_inspection || null,
      alarm_code_hint: securityData.alarm_code_hint || null,
      has_locking_mailbox: securityData.has_locking_mailbox || false,
    }
    const { error } = await supabase.from('systems').update(update).eq('id', system.id)
    setLoading(false)
    if (!error) { setAlert({ type: 'success', msg: 'Security details saved.' }); onUpdated() }
    else setAlert({ type: 'error', msg: 'Error saving security details.' })
  }

  const handleMarkDoneTask = async (item, dateOverride) => {
    setMarkingDone(item.task)
    const dateUsed = dateOverride || new Date().toISOString().split('T')[0]
    const newNextDue = calcNextDue(dateUsed, item.interval)
    const updated = { ...taskDates, [item.task]: { lastCompleted: dateUsed, nextDue: newNextDue, interval: item.interval } }
    setTaskDates(updated)
    await supabase.from('systems').update({ task_dates: updated }).eq('id', system.id)
    await supabase.from('maintenance_logs').insert({
      user_id: system.user_id,
      property_id: system.property_id,
      item_type: 'system',
      item_id: system.id,
      item_name: system.name,
      task_name: item.task,
      completed_date: dateUsed,
    })
    onUpdated()
    setTimeout(() => setMarkingDone(null), 1500)
  }

  const handleGuideMarkComplete = async ({ date }) => {
    const item = selectedTask?.task
    if (!item) return
    await handleMarkDoneTask(item, date)
    setSelectedTask(null)
  }

  const handleGuideMarkIncomplete = async () => {
    const item = selectedTask?.task
    if (!item) return
    const updated = { ...taskDates }
    delete updated[item.task]
    setTaskDates(updated)
    await supabase.from('systems').update({ task_dates: updated }).eq('id', system.id)
    onUpdated()
    setSelectedTask(null)
  }

  return (
    <>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
      <input ref={manualInputRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={handleManualUpload} />

      <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div style={S.modal}>
          <div style={S.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '15px', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, fontFamily: 'Georgia, serif' }}
              >
                ← Back
              </button>
              <div style={{ ...S.title, fontSize: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {mode === 'view'
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><SysIcon size={18} strokeWidth={1.5} color={C.brown} />{system.name}</span>
                  : mode === 'edit' ? 'Edit System' : 'Remove System'}
              </div>
            </div>
          </div>

          <div style={S.body}>
            {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}

            {/* ── VIEW MODE ── */}
            {mode === 'view' && (
              <div>
                {/* Hero icon */}
                <div style={{ ...S.photoBox, flexDirection: 'column', gap: '8px' }}>
                  <SysIcon size={56} strokeWidth={1} color={C.amber} />
                  <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{system.name}</div>
                </div>

                {/* Photo gallery */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={S.sectionTitle}>MY PHOTOS ({photos.length}/{MAX_PHOTOS})</div>
                    {photos.length < MAX_PHOTOS && (
                      <button style={S.photoBtn} onClick={handlePhotoClick}>
                        {photoUploading ? 'Uploading...' : '+ Add Photo'}
                      </button>
                    )}
                  </div>
                  {photos.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      {photos.map((url, i) => (
                        <div key={i} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '1', background: C.bg }}>
                          <img src={url} alt={`System image ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <button onClick={() => handleRemovePhoto(url)} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', color: '#fff', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        </div>
                      ))}
                      {photos.length < MAX_PHOTOS && (
                        <div onClick={handlePhotoClick} style={{ borderRadius: '10px', border: `2px dashed ${C.border}`, aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: '4px' }}>
                          <div style={{ fontSize: '20px', color: C.muted }}>+</div>
                          <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Add</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div onClick={handlePhotoClick} style={{ border: `2px dashed ${C.border}`, borderRadius: '12px', padding: '24px', textAlign: 'center', cursor: 'pointer' }}>
                      <Camera size={28} strokeWidth={1.5} color={C.muted} style={{ marginBottom: '6px' }} />
                      <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Add photos of this system</div>
                      <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>Up to {MAX_PHOTOS} photos</div>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div style={{ marginBottom: '16px' }}>
                  <span style={S.statusBadge(system.status)}>{system.status === 'good' ? '✓ Up to date' : '⚠ Service due'}</span>
                </div>

                {/* Details */}
                <div style={S.sectionTitle}>System Details</div>
                <div style={S.detailGrid}>
                  {[['Manufacturer', system.manufacturer], ['Model', system.model], ['Year Installed', system.installed_year], ['Last Serviced', system.last_serviced]].map(([label, value]) => (
                    <div key={label} style={S.detailItem}>
                      <div style={S.detailLabel}>{label}</div>
                      <div style={value ? S.detailValue : { ...S.detailValue, ...S.detailEmpty }}>{value || 'Not recorded'}</div>
                    </div>
                  ))}
                </div>

                {/* Owner's Manual */}
                <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                  <div style={S.sectionTitle}>Owner's Manual</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                    {manualUrl && (() => {
                      const isDirectPdf = manualUrl?.endsWith('.pdf')
                      return (
                        <a href={manualUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <div style={{ background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, borderRadius: '10px', padding: '12px 16px', fontSize: '14px', color: C.card, fontWeight: '700', fontFamily: 'Georgia, serif', textAlign: 'center', cursor: 'pointer' }}>
                            {isDirectPdf ? '📄 Open Manual PDF' : '📖 Open Manual'}
                          </div>
                        </a>
                      )
                    })()}
                    {system.manufacturer && system.model && (
                      <a href={getManualUrl(system.manufacturer, system.model)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <div style={{ background: C.bg, border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '11px 16px', fontSize: '14px', color: C.amber, fontFamily: 'Calibri, sans-serif', textAlign: 'center', cursor: 'pointer', fontWeight: '600' }}>
                          🔍 Find Manual
                        </div>
                      </a>
                    )}
                    <button
                      onClick={() => manualInputRef.current?.click()}
                      disabled={manualUploading}
                      style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px 16px', fontSize: '13px', color: manualUploading ? C.muted : C.amber, cursor: manualUploading ? 'default' : 'pointer', fontFamily: 'Calibri, sans-serif', textAlign: 'center' }}
                    >
                      {manualUploading ? 'Uploading...' : manualUrl ? '↑ Replace Manual PDF' : '↑ Upload Manual PDF'}
                    </button>
                  </div>
                </div>

                {/* Security Details — shown for security system types */}
                {SECURITY_TYPES.includes(system.type) && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={S.divider} />
                    <div style={S.sectionTitle}>SECURITY DETAILS</div>

                    {system.type === 'security_system' && (
                      <>
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Monitoring Type</div>
                          <select
                            value={securityData.monitoring_type}
                            onChange={e => setSecurityData(p => ({ ...p, monitoring_type: e.target.value }))}
                            style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', marginBottom: '10px' }}
                          >
                            <option value="">Select type</option>
                            <option value="professional">Professional monitoring</option>
                            <option value="self">Self monitored</option>
                            <option value="none">No monitoring</option>
                          </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                          <div>
                            <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Monthly Cost</div>
                            <input type="number" value={securityData.monthly_cost} onChange={e => setSecurityData(p => ({ ...p, monthly_cost: e.target.value }))} placeholder="$0" style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Contract Expires</div>
                            <input type="date" value={securityData.contract_expiration} onChange={e => setSecurityData(p => ({ ...p, contract_expiration: e.target.value }))} style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Alarm Company Phone</div>
                          <input type="tel" value={securityData.alarm_company_phone} onChange={e => setSecurityData(p => ({ ...p, alarm_company_phone: e.target.value }))} placeholder="Emergency line" style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Alarm Code Hint</div>
                          <input value={securityData.alarm_code_hint} onChange={e => setSecurityData(p => ({ ...p, alarm_code_hint: e.target.value }))} placeholder="Hint only — never enter actual code" style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      </>
                    )}

                    {['security_system', 'security_cameras'].includes(system.type) && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <div>
                          <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Camera Count</div>
                          <input type="number" value={securityData.camera_count} onChange={e => setSecurityData(p => ({ ...p, camera_count: e.target.value }))} placeholder="0" style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Sensor Count</div>
                          <input type="number" value={securityData.sensor_count} onChange={e => setSecurityData(p => ({ ...p, sensor_count: e.target.value }))} placeholder="0" style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'Calibri, sans-serif' }}>Last Inspection Date</div>
                      <input type="date" value={securityData.last_inspection} onChange={e => setSecurityData(p => ({ ...p, last_inspection: e.target.value }))} style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={securityData.has_locking_mailbox}
                        onChange={e => setSecurityData(p => ({ ...p, has_locking_mailbox: e.target.checked }))}
                        style={{ width: '16px', height: '16px', accentColor: C.amber, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>🔒 Has locking mailbox</span>
                    </label>

                    <button onClick={saveSecurityData} disabled={loading} style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 0', fontFamily: 'Georgia, serif', fontSize: '14px', cursor: loading ? 'default' : 'pointer', marginBottom: '4px' }}>
                      {loading ? 'Saving...' : 'Save Security Details'}
                    </button>
                  </div>
                )}

                <div style={S.divider} />

                {/* Maintenance Schedule */}
                <div style={S.sectionTitle}>Maintenance Schedule</div>
                <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '10px' }}>
                  Tap any task for step-by-step instructions.
                </div>
                <div style={{ marginBottom: '20px' }}>
                  {schedule.map((item, i) => {
                    const td = taskDates[item.task]
                    const nextDue = td?.nextDue
                    const lastCompleted = td?.lastCompleted
                    const dueStatus = getDueStatus(nextDue)
                    const isDone = markingDone === item.task
                    return (
                      <div key={i}
                        style={{ ...S.scheduleRow(hoveredTask === i), cursor: 'default' }}
                        onMouseEnter={() => setHoveredTask(i)}
                        onMouseLeave={() => setHoveredTask(null)}
                      >
                        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setSelectedTask({ task: item, applianceName: system.name, applianceType: system.type })}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: C.brown }}>{item.task}</div>
                          <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>{item.interval}</div>
                          {lastCompleted && (
                            <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>
                              Done {formatDate(lastCompleted)}
                              {nextDue && <span style={{ color: dueStatus === 'overdue' ? C.danger : dueStatus === 'soon' ? C.amber : C.muted }}> · Next: {formatDate(nextDue)}</span>}
                            </div>
                          )}
                          <div style={S.howToHint}>Tap for how-to guide →</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', marginLeft: '10px' }}>
                          <div style={S.tag(item.type)}>{item.type === 'auto_order' ? 'Order Parts' : item.type === 'contractor' ? 'Contractor' : 'DIY'}</div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkDoneTask(item) }}
                            disabled={isDone}
                            style={{ background: isDone ? C.successBg : 'transparent', border: `1px solid ${isDone ? C.success : C.amber}`, borderRadius: '6px', padding: '3px 10px', fontSize: '10px', color: isDone ? C.success : C.amber, cursor: isDone ? 'default' : 'pointer', fontFamily: 'Calibri, sans-serif', whiteSpace: 'nowrap' }}
                          >
                            {isDone ? '✓ Done!' : 'Mark Done'}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div style={S.divider} />

                {/* Notes */}
                <div style={S.sectionTitle}>Notes</div>
                <textarea style={S.notesBox} placeholder="e.g. Tank is 1,000 gallons, pumped last in 2021 by Roto-Rooter." value={notes} onChange={e => { setNotes(e.target.value); setNotesSaved(false) }} />
                <button style={S.saveNotesBtn} onClick={saveNotes} disabled={loading}>{notesSaved ? '✓ Saved' : loading ? 'Saving...' : 'Save Notes'}</button>

                <div style={S.btnRow}>
                  <button style={S.editBtn} onClick={() => { setMode('edit'); setAlert(null) }}>Edit Details</button>
                  <button style={S.deleteBtn} onClick={() => setMode('confirmDelete')}>Remove</button>
                </div>
              </div>
            )}

            {/* ── EDIT MODE ── */}
            {mode === 'edit' && (
              <div>
                <div style={S.sectionTitle}>Update System Details</div>
                <label style={{ ...S.sectionTitle, marginBottom: '5px' }}>Manufacturer</label>
                <input style={S.input} value={editForm.manufacturer} onChange={setEdit('manufacturer')} placeholder="e.g. Tesla, Rheem" autoComplete="off" />
                <label style={{ ...S.sectionTitle, marginBottom: '5px' }}>Model</label>
                <input style={S.input} value={editForm.model} onChange={setEdit('model')} placeholder="e.g. Powerwall 2" autoComplete="off" />
                <label style={{ ...S.sectionTitle, marginBottom: '5px' }}>Year Installed</label>
                <select style={S.select} value={editForm.installed_year} onChange={setEdit('installed_year')}>
                  <option value="">Select year...</option>
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <label style={{ ...S.sectionTitle, marginBottom: '5px' }}>Last Serviced</label>
                <input style={S.input} value={editForm.last_serviced} onChange={setEdit('last_serviced')} placeholder="e.g. March 2024" autoComplete="off" />
                <div style={S.btnRow}>
                  <button style={S.editBtn} onClick={() => { setMode('view'); setAlert(null) }}>Cancel</button>
                  <button style={S.saveBtn} onClick={saveEdit} disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </div>
            )}

            {/* ── CONFIRM DELETE ── */}
            {mode === 'confirmDelete' && (
              <div>
                <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '16px', marginBottom: '20px', fontSize: '14px', color: C.danger, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                  Are you sure you want to remove <strong>{system.name}</strong>? This will delete its maintenance schedule and all records.
                </div>
                <div style={S.btnRow}>
                  <button style={S.editBtn} onClick={() => setMode('view')}>Cancel</button>
                  <button style={{ ...S.saveBtn, background: C.danger }} onClick={deleteSystem} disabled={loading}>{loading ? 'Removing...' : 'Yes, Remove It'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedTask && (
        <TaskDetailSheet
          task={selectedTask.task}
          applianceName={selectedTask.applianceName}
          applianceType={selectedTask.applianceType}
          taskDates={taskDates}
          onComplete={handleGuideMarkComplete}
          onMarkIncomplete={handleGuideMarkIncomplete}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </>
  )
}